(() => {
  if (window.ShopifyProductPricing) return;

  const query = `mutation ProductPricePreview($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        checkoutUrl
        cost { totalAmount { amount currencyCode } }
        lines(first: 100) {
          pageInfo { hasNextPage }
          nodes {
            quantity attributes { key value }
            merchandise { ... on ProductVariant { id } }
            cost {
              amountPerQuantity { amount currencyCode }
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
          }
        }
      }
      userErrors { message }
      warnings { code }
    }
  }`;
  const groupProperty = '_flowtica_price_group';
  const componentProperty = '_flowtica_price_component';
  const standaloneCart = { items: [], attributes: {}, note: '', discount_codes: [] };
  const cacheLifetime = 5 * 60 * 1000;
  const attributes = (properties) => Object.entries(properties || {}).map(([key, value]) => ({ key, value: String(value) }));
  const gid = (type, id) => String(id).startsWith('gid://') ? String(id) : `gid://shopify/${type}/${id}`;
  const cents = (money) => {
    if (!money || !/^\d+(\.\d{1,2})?$/.test(money.amount)) throw new Error('Invalid Shopify price');
    const [whole, fraction = ''] = money.amount.split('.');
    const amount = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(amount)) throw new Error('Invalid Shopify price');
    return amount;
  };
  const selectionTotal = (groups) => Array.from(groups.values()).reduce((total, group) => total + group.total, 0);

  window.ShopifyProductPricing = class ShopifyProductPricing {
    constructor(offer) {
      this.offer = offer;
      this.root = offer.closest('product-info');
      this.currency = offer.dataset.currency;
      this.locale = offer.dataset.locale;
      this.strings = JSON.parse(offer.querySelector('[data-price-preview-strings]').textContent);
      this.group = crypto.randomUUID();
      this.generation = 0;
      this.candidates = new Map();
      this.cache = new Map();
      this.addonDisplays = new Map();
      this.cartRevision = 0;
      this.onInput = () => this.schedule(this.status !== 'pending' && !(this.cartExpires > Date.now()));
      this.onVisibility = () => { if (!document.hidden && !(this.cartExpires > Date.now())) this.schedule(true); };
      this.root.addEventListener('change', this.onInput);
      this.root.addEventListener('input', this.onInput);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!this.busy && event.cartData !== this.lastCartResponse) { this.invalidate(); this.schedule(true); }
      });
    }

    disconnect() {
      this.generation += 1;
      this.root.removeEventListener('change', this.onInput);
      this.root.removeEventListener('input', this.onInput);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.unsubscribe?.();
      this.controller?.abort();
      clearTimeout(this.timer);
    }

    get addonInputs() {
      return Array.from(this.root.querySelectorAll('[data-cart-addon-input], input[name^="complementary_products"]'));
    }

    source(input) {
      return input.closest('[data-accessory-card]')?.querySelector(`[data-accessory-variant][data-variant-id="${input.value}"]`) || input;
    }

    entry(input, index) {
      return { id: String(input.value), quantity: Math.max(1, Number(input.dataset.quantity || 1)),
        component: `addon-${index}`, input, source: this.source(input) };
    }

    selection() {
      const form = this.root.querySelector('product-form form');
      if (!form || this.root.querySelector('scribe-product-v2')?.quantityCollapsed) return [];
      const data = new FormData(form);
      const id = data.get('id');
      const quantity = Number(data.get('quantity') || 1);
      if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return [];
      const properties = Object.fromEntries(Array.from(data.entries()).filter(([key]) => /^properties\[.+\]$/.test(key))
        .map(([key, value]) => [key.slice(11, -1), String(value)]));
      const entries = [{ id: String(id), quantity, component: 'main', properties, sellingPlan: data.get('selling_plan') || null }];
      this.addonInputs.forEach((input, index) => {
        if (input.checked && !input.disabled) entries.push(this.entry(input, index));
      });
      return entries;
    }

    codes(entries) {
      const card = this.offer.querySelector('[data-discount-offer-current] [data-discount-offer-card]');
      return Array.from(new Set([card, ...entries.map(entry => entry.source)].filter(node => node && this.offer.isOfferActive(node))
        .map(node => node.dataset.offerCode).filter(Boolean).map(code => code.toUpperCase())));
    }

    signature(entries = this.selection()) {
      return JSON.stringify({ lines: entries.map(({ id, quantity, component, properties, sellingPlan }) => ({ id, quantity, component, properties, sellingPlan })),
        codes: this.codes(entries) });
    }

    cartCodes(cart) {
      const codes = (cart.discount_codes || []).map(discount => discount.code);
      const discounts = [...(cart.cart_level_discount_applications || []), ...(cart.items || [])
        .flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))];
      discounts.filter(discount => discount?.type === 'discount_code').forEach(discount => codes.push(discount.title));
      return Array.from(new Set(codes.filter(Boolean).map(code => code.toUpperCase())));
    }

    async readCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error(this.strings.unavailable);
      return response.json();
    }

    invalidate() {
      this.cache.clear();
      this.cartSnapshot = null;
      this.cartRequest = null;
      this.cartRevision += 1;
    }

    async readPreviewCart(force = false) {
      if (!force && this.cartSnapshot && this.cartExpires > Date.now()) return this.cartSnapshot;
      if (this.cartRequest) return this.cartRequest;
      const revision = this.cartRevision;
      const request = this.readCart().then(cart => {
        if (revision === this.cartRevision) {
          this.cartSnapshot = cart;
          this.cartExpires = Date.now() + cacheLifetime;
        }
        return cart;
      }).finally(() => { if (this.cartRequest === request) this.cartRequest = null; });
      this.cartRequest = request;
      return request;
    }

    cachedQuote(input, maxAge = cacheLifetime) {
      const cached = this.cache.get(JSON.stringify(input));
      return cached && Date.now() - cached.created < maxAge ? cached.result : null;
    }

    schedule(force = false) {
      if (this.busy) return;
      const entries = this.selection();
      const key = this.signature(entries);
      if (!force && key === this.key) return;
      const changed = key !== this.key;
      if (changed && this.committed) {
        this.committed = false;
        this.group = crypto.randomUUID();
      }
      const cart = entries.length === 1 ? standaloneCart : this.cartSnapshot;
      const input = !this.committed && entries.length && cart ? this.quoteInput(entries, cart, this.group) : null;
      const cached = input ? this.cachedQuote(input, Infinity) : null;
      const fresh = input && this.cachedQuote(input) && (entries.length === 1 || this.cartExpires > Date.now());
      this.result = entries.length ? cached || (!changed ? this.result : null) : null;
      if (changed) this.candidates.clear();
      this.key = key;
      this.generation += 1;
      this.controller?.abort();
      clearTimeout(this.timer);
      this.status = entries.length ? fresh && !force ? 'ready' : 'pending' : 'empty';
      this.render();
      if (!entries.length) return;
      const generation = this.generation;
      this.timer = setTimeout(() => this.refresh(entries, generation, force), 250);
    }

    async refresh(entries, generation, force = false) {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      try {
        const cart = await this.readPreviewCart(force);
        if (generation !== this.generation) return;
        const committed = this.committed && this.applyCart(cart);
        if (!committed) {
          const previewCart = entries.length === 1 ? standaloneCart : cart;
          const result = await this.quote(entries, previewCart, signal, this.group, force ? 0 : cacheLifetime);
          if (generation !== this.generation) return;
          this.result = result;
          this.status = 'ready';
          this.render();
        }
        const candidateGroup = committed ? `${this.group}-preview` : this.group;
        for (const [index, input] of this.addonInputs.entries()) {
          if (generation !== this.generation) return;
          if (input.checked || input.disabled) continue;
          const entry = this.entry(input, index);
          const group = input.closest('[data-sku-option-group][data-selection-mode="single"]');
          const alternate = entries.filter(item => !group || !item.input || !group.contains(item.input));
          alternate.push(entry);
          alternate.sort((a, b) => (a.component === 'main' ? -1 : Number(a.component.slice(6)))
            - (b.component === 'main' ? -1 : Number(b.component.slice(6))));
          try {
            const candidate = await this.quote(alternate, cart, signal, candidateGroup);
            if (generation !== this.generation) return;
            this.candidates.set(entry.component, candidate.groups.get(entry.component));
            this.offer.refreshAddonPrices(this.root);
          } catch (error) {
            if (signal.aborted) break;
            this.candidates.set(entry.component, null);
            this.offer.refreshAddonPrices(this.root);
          }
        }
      } catch (error) {
        if (generation !== this.generation || this.controller.signal.aborted) return;
        console.warn('[Product price preview]', error.message);
        this.status = 'error';
        this.render();
      }
    }

    quoteInput(entries, cart, group) {
      const lines = cart.items.map(item => ({ merchandiseId: gid('ProductVariant', item.variant_id), quantity: item.quantity,
        attributes: attributes(item.properties), ...(item.selling_plan_allocation ? { sellingPlanId: gid('SellingPlan', item.selling_plan_allocation.selling_plan.id) } : {}) }));
      for (const entry of entries) lines.push({ merchandiseId: gid('ProductVariant', entry.id), quantity: entry.quantity,
        attributes: attributes({ ...entry.properties, [groupProperty]: group, [componentProperty]: entry.component }),
        ...(entry.sellingPlan ? { sellingPlanId: gid('SellingPlan', entry.sellingPlan) } : {}) });
      return { lines, buyerIdentity: { countryCode: this.offer.dataset.country },
        discountCodes: Array.from(new Set([...this.cartCodes(cart), ...this.codes(entries)])),
        attributes: attributes(cart.attributes), note: cart.note || '' };
    }

    async quote(entries, cart, signal, group = this.group, maxAge = cacheLifetime) {
      if (cart.items.some(item => item.remote || item.parent_relationship?.parent)) throw new Error(this.strings.unavailable);
      const input = this.quoteInput(entries, cart, group);
      if (input.lines.length > 100) throw new Error(this.strings.unavailable);
      const key = JSON.stringify(input);
      const cached = this.cachedQuote(input, maxAge);
      if (cached) return cached;
      const response = await fetch(this.offer.dataset.previewEndpoint, {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { input } }), signal: AbortSignal.any([signal, AbortSignal.timeout(12000)]),
      });
      if (!response.ok) throw new Error(`Shopify preview HTTP ${response.status}`);
      const payload = await response.json();
      signal.throwIfAborted();
      const result = payload.data?.cartCreate;
      if (payload.errors?.length) throw new Error('Shopify preview GraphQL error');
      if (result?.userErrors?.length) throw new Error('Shopify preview cart input error');
      // 优惠码未被采用时，Shopify 仍会返回按可用优惠计算的有效金额。
      const blockingWarnings = result?.warnings?.filter(warning => !warning.code?.startsWith('DISCOUNT_')) || [];
      if (blockingWarnings.length) throw new Error(`Shopify preview cart warning: ${blockingWarnings.map(warning => warning.code).join(', ')}`);
      if (!result?.cart || result.cart.lines.pageInfo.hasNextPage) throw new Error('Shopify preview incomplete cart');
      if (result.cart.cost.totalAmount.currencyCode !== this.currency) throw new Error('Shopify preview currency mismatch');
      const groups = new Map();
      for (const line of result.cart.lines.nodes) {
        const properties = Object.fromEntries(line.attributes.map(attribute => [attribute.key, attribute.value]));
        if (properties[groupProperty] !== group) continue;
        this.addGroup(groups, properties[componentProperty], line.quantity, cents(line.cost.totalAmount));
      }
      for (const entry of entries) {
        if (groups.get(entry.component)?.quantity !== entry.quantity) throw new Error('Shopify preview selected quantity mismatch');
      }
      const view = { total: selectionTotal(groups), groups, variantId: entries[0].id, checkoutUrl: result.cart.checkoutUrl };
      this.cache.delete(key);
      if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, { result: view, created: Date.now() });
      return view;
    }

    addGroup(groups, component, quantity, total) {
      if (!Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(total) || total < 0) throw new Error(this.strings.unavailable);
      const group = groups.get(component) || { quantity: 0, total: 0, min: Infinity, max: 0 };
      const unit = Math.round(total / quantity);
      group.quantity += quantity;
      group.total += total;
      group.min = Math.min(group.min, unit);
      group.max = Math.max(group.max, unit);
      groups.set(component, group);
    }

    applyCart(cart) {
      if (!Number.isSafeInteger(cart.total_price) || cart.currency !== this.currency) throw new Error(this.strings.unavailable);
      const groups = new Map();
      for (const item of cart.items) {
        if (item.properties?.[groupProperty] === this.group) this.addGroup(groups, item.properties[componentProperty], item.quantity, item.final_line_price);
      }
      const main = cart.items.find(item => item.properties?.[groupProperty] === this.group && item.properties[componentProperty] === 'main');
      const entries = this.selection();
      if (!main || !entries.length || entries.some(entry => groups.get(entry.component)?.quantity !== entry.quantity
        || !cart.items.some(item => item.properties?.[groupProperty] === this.group
          && item.properties[componentProperty] === entry.component && String(item.variant_id) === entry.id))) {
        this.committed = false;
        this.group = crypto.randomUUID();
        this.result = null;
        return false;
      }
      this.result = { total: selectionTotal(groups), groups, variantId: String(main?.variant_id || '') };
      this.status = 'cart';
      this.render();
      return true;
    }

    get mainGroup() {
      const variantId = this.offer.dataset.currentVariantId;
      if (this.result?.variantId === variantId) return this.result.groups.get('main');
      return this.status === 'pending' && this.displayMain?.variantId === variantId ? this.displayMain.group : null;
    }

    getPrice(variantId) {
      if (String(variantId) !== this.offer.dataset.currentVariantId) return null;
      if (this.mainGroup) return this.mainGroup.min;
      return !this.committed && (this.selection().length === 1 || this.status === 'pending')
        ? this.offer.getDefaultPrice(variantId) : Number(this.offer.dataset.originalPrice);
    }

    render() {
      if (this.result?.variantId === this.offer.dataset.currentVariantId && this.result.groups.has('main')) {
        this.displayMain = { variantId: this.result.variantId, group: this.result.groups.get('main') };
      }
      this.offer.refreshPrices(false);
      const total = this.root.querySelector('[data-order-total], [data-product-summary-total]');
      if (total) {
        if (this.result) this.offer.writePrice(total, this.result.total);
        else total.textContent = this.status === 'empty' ? '—' : this.status === 'pending' ? this.strings.calculating : '—';
        total.setAttribute('aria-busy', String(this.status === 'pending'));
      }
      const original = this.root.querySelector('[data-product-summary-original]');
      if (original) original.hidden = true;
      const summaryCompare = this.root.querySelector('[data-product-summary-total]')?.closest('.quantity__label')?.querySelector('.compare_at_price');
      if (summaryCompare) summaryCompare.hidden = true;
      const status = this.root.querySelector('[data-product-price-status]');
      if (status) {
        status.textContent = this.status === 'error' ? this.strings.unavailable
          : this.status === 'cart' ? this.strings.cartUpdated
          : this.status === 'ready' ? this.strings.preview : '';
        status.hidden = !status.textContent;
      }
      this.offer.dataset.priceStatus = this.status;
    }

    renderAddons() {
      this.addonInputs.forEach((input, index) => {
        const card = input.closest('[data-accessory-card], .membership-card');
        if (!card) return;
        const source = this.source(input);
        let group = input.checked ? this.result?.groups.get(`addon-${index}`) : this.candidates.get(`addon-${index}`);
        const displayKey = `${index}:${input.value}:${input.dataset.quantity || 1}`;
        if (group) this.addonDisplays.set(displayKey, group);
        else if (this.status === 'pending' || (!input.checked && this.status !== 'error' && !this.candidates.has(`addon-${index}`))) {
          group = this.addonDisplays.get(displayKey);
        }
        const quantity = group?.quantity || Math.max(1, Number(input.dataset.quantity || 1));
        const original = Number(source.dataset.originalPrice || source.dataset.price || 0) * quantity;
        const price = group?.total ?? original;
        const unavailable = !group && (this.status === 'error' || this.candidates.has(`addon-${index}`));
        const pending = !group && !unavailable && this.status !== 'empty';
        input.dataset.price = String(price / quantity);
        const node = card.querySelector('[data-accessory-price]');
        if (node) {
          if (pending) node.textContent = this.strings.calculating;
          else { this.offer.writePrice(node, price); node.prepend('+'); }
        }
        const compare = card.querySelector('[data-addon-compare-price]');
        const reference = price < original ? original : Number(source.dataset.compareAtPrice || 0) * quantity;
        if (compare) {
          compare.hidden = pending || reference <= price;
          this.offer.writePrice(compare.querySelector('[data-addon-compare-amount]'), reference, false);
        }
        const label = card.querySelector('[data-addon-quantity-label]');
        if (label) {
          label.textContent = this.strings.quantityTotal.replace('[quantity]', String(quantity));
          label.hidden = quantity < 2;
        }
        card.toggleAttribute('data-price-unavailable', unavailable);
        const status = card.querySelector('[data-addon-price-status]');
        if (status) {
          status.textContent = unavailable ? this.strings.addonUnavailable : '';
          status.hidden = !unavailable;
        }
        node?.setAttribute('aria-busy', String(pending || (input.checked && this.status === 'pending')));
      });
    }

    async createCheckout() {
      if (this.busy) throw new Error(this.strings.adding);
      const entries = this.selection();
      if (!entries.length) throw new Error(this.strings.unavailable);
      this.busy = true;
      try {
        const result = await this.quote(entries, standaloneCart, new AbortController().signal, crypto.randomUUID(), 0);
        if (!result.checkoutUrl) throw new Error(this.strings.unavailable);
        return result.checkoutUrl;
      } finally {
        this.busy = false;
        if (this.offer.isConnected) this.schedule(true);
      }
    }

    async addToCart(sections) {
      if (this.busy) throw new Error(this.strings.adding);
      const entries = this.selection();
      if (!entries.length) throw new Error(this.strings.unavailable);
      const key = this.signature(entries);
      if (this.committed) this.group = crypto.randomUUID();
      this.committed = false;
      const submittedGroup = this.group;
      this.busy = true;
      this.generation += 1;
      this.controller?.abort();
      this.invalidate();
      clearTimeout(this.timer);
      let added = false;
      let addedResponse;
      let warning;
      try {
        try {
          const cart = await this.readCart();
          const configuredCodes = this.codes(entries);
          const items = entries.map(entry => ({ id: entry.id, quantity: entry.quantity,
            properties: { ...entry.properties, [groupProperty]: submittedGroup, [componentProperty]: entry.component },
            ...(entry.sellingPlan ? { selling_plan: entry.sellingPlan } : {}) }));
          if (!cart.items.some(item => item.properties?.[groupProperty] === submittedGroup)) {
            const response = await fetch(window.routes.cart_add_url, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
              signal: AbortSignal.timeout(15000),
            });
            addedResponse = await response.json();
            if (!response.ok || addedResponse.status) throw new Error(addedResponse.description || this.strings.unavailable);
          } else addedResponse = {};
          added = true;
          {
            const current = await this.readCart();
            const codes = Array.from(new Set([...this.cartCodes(current), ...configuredCodes]));
            const update = await fetch(window.routes.cart_update_url, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ ...(configuredCodes.length ? { discount: codes.join(',') } : {}), sections, sections_url: window.location.pathname }),
              signal: AbortSignal.timeout(12000),
            });
            if (!update.ok) throw new Error(this.strings.discountFailed);
            const updated = await update.json();
            if (updated.status) throw new Error(this.strings.discountFailed);
            addedResponse.sections = updated.sections;
          }
        } catch (error) {
          if (!added) {
            const current = await this.readCart().catch(() => null);
            if (!current?.items.some(item => item.properties?.[groupProperty] === submittedGroup)) {
              throw error;
            }
            added = true;
            addedResponse = {};
            warning = this.strings.addedNeedsRefresh;
          } else {
            warning = this.strings.discountFailed;
            delete addedResponse.sections;
          }
        }
        this.committed = true;
        this.invalidate();
        try {
          const cart = await this.readCart();
          this.cartSnapshot = cart;
          this.cartExpires = Date.now() + cacheLifetime;
          if (key === this.signature()) this.applyCart(cart);
          if (!sections.every(section => addedResponse.sections?.[section])) {
            const response = await fetch(`${window.routes.cart_url}?sections=${encodeURIComponent(sections.join(','))}`, { signal: AbortSignal.timeout(12000) });
            if (response.ok) addedResponse.sections = await response.json();
          }
          const item = cart.items.find(line => line.properties?.[groupProperty] === submittedGroup);
          this.lastCartResponse = { ...cart, sections: addedResponse.sections, key: item?.key, warning };
          return this.lastCartResponse;
        } catch (error) {
          this.status = 'error';
          this.result = null;
          this.render();
          return { ...addedResponse, warning: this.strings.addedNeedsRefresh };
        }
      } finally {
        this.busy = false;
        if (this.offer.isConnected) this.schedule(true);
      }
    }
  };
})();
